import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { TransactionEntity } from '@actual-app/core/types/models';

import { Icono } from '#components/mobile/ui/Icono';
import { color, densidad, TACTIL } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { usePayeesById } from '#hooks/usePayees';
import { useQuery } from '#hooks/useQuery';

type Fila = Pick<
  TransactionEntity,
  'id' | 'date' | 'amount' | 'payee' | 'notes'
>;

/**
 * Movimientos de una categoría en un mes, bajo la gráfica de «Evolución»:
 * «Movimientos de <mes> · N · total X», plegable con un toque. La consulta
 * se filtra por categoría y mes (no carga el historial) con el mismo criterio
 * que la gráfica de gasto: cuentas del presupuesto y sin traspasos internos.
 * Tocar un movimiento abre su editor.
 */
export function MovimientosMes({
  categoryId,
  mes,
  abierto,
  onAlternar,
}: {
  categoryId: string;
  mes: string;
  abierto: boolean;
  onAlternar: () => void;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const { data: payees = {} } = usePayeesById();

  const { data } = useQuery<Fila>(
    () =>
      abierto
        ? q('transactions')
            .options({ splits: 'inline' })
            .filter({
              category: categoryId,
              date: { $transform: '$month', $eq: mes },
              'account.offbudget': false,
              $or: [
                { 'payee.transfer_acct': null },
                { 'payee.transfer_acct.offbudget': true },
              ],
            })
            .select(['id', 'date', 'amount', 'payee', 'notes'])
            .orderBy({ date: 'desc' })
        : null,
    [abierto, categoryId, mes],
  );
  const filas = useMemo(() => data ?? [], [data]);
  // Mismo signo que la barra: gasto en positivo.
  const total = -filas.reduce((s, f) => s + f.amount, 0);
  const nombreMes = monthUtils.format(mes, 'MMMM yyyy', locale);

  return (
    <View data-testid="movimientos-mes" style={{ gap: 0 }}>
      <Button
        variant="bare"
        onPress={onAlternar}
        aria-expanded={abierto}
        data-testid="movimientos-alternar"
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          gap: 6,
          minHeight: TACTIL,
          padding: 0,
          borderRadius: 0,
          borderTop: `1px dashed ${color.line2}`,
          ...densidad.pequeno,
          color: abierto ? color.fg2 : color.accent,
          backgroundColor: 'transparent',
        }}
      >
        <Icono nombre={abierto ? 'cu' : 'cd'} size={14} />
        {abierto ? (
          <PrivacyFilter>
            <span data-testid="movimientos-cabecera">
              {t('Transactions of {{month}} · {{count}} · total {{amount}}', {
                month: nombreMes,
                count: filas.length,
                amount: format(total, 'financial'),
              })}
            </span>
          </PrivacyFilter>
        ) : (
          t('See transactions of {{month}}', { month: nombreMes })
        )}
      </Button>
      {abierto && filas.length === 0 && data && (
        <Text
          style={{ ...densidad.pequeno, color: color.fg3, paddingBottom: 8 }}
        >
          <Trans>No transactions this month.</Trans>
        </Text>
      )}
      {abierto &&
        filas.map(f => (
          <Button
            key={f.id}
            variant="bare"
            onPress={() => void navigate(`/transactions/${f.id}`)}
            data-testid="movimiento-fila"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              minHeight: TACTIL,
              padding: 0,
              borderRadius: 0,
              boxShadow: `inset 0 1px 0 ${color.line}`,
              backgroundColor: 'transparent',
              textAlign: 'left',
            }}
          >
            <Text
              style={{
                ...densidad.pequeno,
                color: color.fg3,
                width: 44,
                flexShrink: 0,
              }}
            >
              {monthUtils.format(f.date, 'd MMM', locale).replace('.', '')}
            </Text>
            <Text
              style={{
                ...densidad.nombre,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: color.fg,
              }}
            >
              {(f.payee && payees[f.payee]?.name) || f.notes || t('No payee')}
            </Text>
            <PrivacyFilter>
              <Text
                style={{
                  ...densidad.cifra,
                  color: f.amount < 0 ? color.fg : color.ok,
                }}
              >
                {format(f.amount, 'financial')}
              </Text>
            </PrivacyFilter>
          </Button>
        ))}
    </View>
  );
}
